import React, { useState, useEffect } from 'react';
import { Plus, AlertTriangle } from 'lucide-react'; // Removed Save, X, Loader2

import { Button } from '../../components/ui/Button'; // Path relative to src/pages/admin/
// Removed Input, Textarea, Card components as they are used in ProductForm
// import { Input } from '../../components/ui/input';
// import { Textarea } from '../../components/ui/textarea';
// import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';

import { ProductList } from '../../components/ProductList'; // Path relative to src/pages/admin/
import { useAuth } from '../../contexts/AuthContext';
// ProductForm will be imported from its new location later if this component needs it directly,
// For now, AdminPanel renders ProductForm internally. If ProductForm is also moved, this import will change.
import ProductForm from '../../components/admin/ProductForm'; // Placeholder, will be ../../components/admin/ProductForm
import { Product } from '../../types/Product'; // Import Product from new location
import { useMemo } from 'react'; // Import useMemo
import { HARDCODED_CATEGORIES } from '../../config/categories'; // Import hardcoded categories

// Copied ProductForm from App.tsx - This will be moved to its own file in the next step
// For now, defining it here to keep AdminPanelPage self-contained until ProductForm is moved.
// END TEMPORARY ProductForm // This line and above ProductForm definition will be removed


const AdminPanelPage: React.FC = () => {
  const { token } = useAuth();
  const BACKEND_URL_ROOT = process.env.REACT_APP_BACKEND_URL_ROOT || 'http://localhost:3001';
  const API_PRODUCTS_URL = `${BACKEND_URL_ROOT}/api/products`;

  const [products, setProducts] = useState<Product[]>([]);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const existingCategories = useMemo(() => {
    const dynamicCategories = products.length > 0
      ? Array.from(new Set(products.map(p => p.category).filter(Boolean)))
      : [];

    // Combine hardcoded and dynamic, ensuring uniqueness and sorting
    const combined = Array.from(new Set([...HARDCODED_CATEGORIES, ...dynamicCategories])).sort();

    return combined; // This list is directly used by ProductForm's dropdown
  }, [products]);

  const handleAddProduct = () => {
    setEditingProduct({
      id: 'new', // Temporary ID for new products
      name: '',
      description: '',
      price: 0,
      imageUrl: '',
      category: '',
      specifications: {},
    });
  };

  const handleEditProduct = (id: string) => {
    const productToEdit = products.find((p) => p.id === id);
    if (productToEdit) {
      setEditingProduct(productToEdit);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!token) {
      setError("Authentication token not found. Please login.");
      return;
    }
    setError(null);
    try {
      const response = await fetch(`${API_PRODUCTS_URL}/${id}`, { // Use API_PRODUCTS_URL
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.status === 204) {
        setProducts(products.filter((p) => p.id !== id));
      } else if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to delete product and parse error response.' }));
        throw new Error(errorData.message || `Failed to delete product. Status: ${response.status}`);
      } else {
         // success if not 204 but ok (e.g. 200 with body)
        setProducts(products.filter((p) => p.id !== id));
      }
    } catch (err: any) {
      console.error("Delete product error:", err);
      setError(err.message || 'An unexpected error occurred while deleting the product.');
    }
  };

  const handleSaveProduct = async (productData: Product) => {
    if (!token) {
      setError("Authentication token not found. Please login.");
      return;
    }
    setIsSaving(true);
    setError(null);

    const isNewProduct = productData.id === 'new';
    const url = isNewProduct ? API_PRODUCTS_URL : `${API_PRODUCTS_URL}/${productData.id}`; // Use API_PRODUCTS_URL
    const method = isNewProduct ? 'POST' : 'PUT';

    let payload: any = { ...productData };
    if (isNewProduct) {
      delete payload.id; // Remove temporary 'new' id before sending to backend
    }

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || `Failed to save product. Status: ${response.status}`);
      }
      if (isNewProduct) {
        setProducts([...products, result]);
      } else {
        setProducts(products.map((p) => (p.id === result.id ? result : p)));
      }
      setEditingProduct(null);
    } catch (err: any) {
      console.error("Save product error:", err);
      setError(err.message || 'An unexpected error occurred while saving the product.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(API_PRODUCTS_URL); // Use API_PRODUCTS_URL
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Failed to fetch products and parse error response.' }));
          throw new Error(errorData.message || `Failed to fetch products. Status: ${response.status}`);
        }
        const data: Product[] = await response.json();
        setProducts(data);
      } catch (err: any) {
        console.error("Fetch products error:", err);
        setError(err.message || 'An unexpected error occurred while fetching products.');
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, [API_PRODUCTS_URL]); // Add API_PRODUCTS_URL here


  return (
    <div className="admin-panel-container">
      <h2 className="admin-panel-title">Admin Dashboard</h2>
      <div className="admin-panel-actions">
        <Button onClick={handleAddProduct}>
          <Plus className="icon" /> Add Product
        </Button>
      </div>

      {error && <div className="error-message" role="alert" style={{color: 'red', marginBottom: '1rem'}}><AlertTriangle className="icon"/> {error}</div>}

      {editingProduct ? (
        <ProductForm
          product={editingProduct}
          onSave={handleSaveProduct}
          onCancel={handleCancelEdit}
          isSaving={isSaving}
          error={error} /* Pass error to ProductForm if it should display specific save errors */
          existingCategories={existingCategories} // Pass the new prop
        />
      ) : (
        <ProductList
          products={products}
          onEdit={handleEditProduct}
          onDelete={handleDeleteProduct}
          isLoading={isLoading}
          error={error} /* Pass general loading error to ProductList */
        />
      )}
    </div>
  );
};

export default AdminPanelPage;
